(() => {
  'use strict';
  const root = window.Shopify?.routes?.root || '/';
  const config = JSON.parse(document.querySelector('[data-el-cart-sync-config]')?.textContent || '{}');
  let queue = Promise.resolve(), pendingSync = null, version = 0, reviewMessage = '', checkoutPending = false;
  const money = cents => new Intl.NumberFormat(document.documentElement.lang || 'en', {style:'currency', currency:config.currency || 'USD'}).format(cents / 100);
  const containers = () => [...document.querySelectorAll('cart-drawer,.el-system-page--cart')];
  const sectionId = node => node.closest('.shopify-section')?.id.replace('shopify-section-', '');
  const sectionIds = () => [...new Set(containers().map(sectionId).filter(Boolean))];
  const failure = (message, details = {}) => Object.assign(new Error(message), details);
  const serialize = operation => {
    const result = queue.then(operation, operation);
    queue = result.catch(() => {});
    return result;
  };
  const status = message => containers().forEach(node => {
    let notice = node.querySelector('[data-el-cart-sync-status]');
    if (!notice) {
      notice = document.createElement('p');
      notice.dataset.elCartSyncStatus = '';
      notice.className = 'el-cart-sync-status';
      notice.setAttribute('role', 'status');
      (node.querySelector('[slot="footer"],.el-system-page__summary') || node).prepend(notice);
    }
    notice.textContent = message || '';
    notice.hidden = !message;
  });
  const request = async (url, options = {}) => {
    const response = await fetch(url, {...options, cache:'no-store', headers:{Accept:'application/json', ...options.headers}, signal:AbortSignal.timeout(12000)});
    let body;
    try { body = await response.json(); } catch (_) {
      throw failure('The cart response could not be read.', {code:'INVALID_RESPONSE', status:response.status, confirmed:response.status >= 400 && response.status < 500});
    }
    if (!response.ok) throw failure(typeof body.description === 'string' ? body.description : typeof body.message === 'string' ? body.message : 'The cart could not be updated.', {code:'CART_REQUEST', status:response.status, confirmed:response.status >= 400 && response.status < 500});
    return body;
  };
  const signature = item => JSON.stringify([item.variant_id, item.properties || {}, item.selling_plan_allocation?.selling_plan?.id || null]);
  const differences = (before, after) => (before.items || []).flatMap(old => {
    const item = after.items.find(next => next.key === old.key || signature(next) === signature(old));
    return item && old.quantity === item.quantity && old.final_line_price !== item.final_line_price
      ? [`${item.product_title || item.title}: ${money(old.final_line_price)} → ${money(item.final_line_price)}`] : [];
  });
  const prepareSections = (cart, ids) => {
    if (!Array.isArray(cart?.items) || !Number.isFinite(cart.total_price) || !Number.isInteger(cart.item_count)) throw failure('The cart response is incomplete.', {code:'INVALID_CART'});
    const plans = [];
    for (const id of ids) {
      const html = cart.sections?.[id];
      if (typeof html !== 'string' || !html.trim()) throw failure('The updated cart could not be displayed.', {code:'MISSING_SECTION'});
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      for (const selector of ['cart-drawer', '.el-system-page--cart']) {
        const current = document.querySelector(selector);
        if (!current || sectionId(current) !== id) continue;
        const next = parsed.querySelector(selector);
        if (!next) throw failure('The updated cart could not be displayed.', {code:'INVALID_SECTION'});
        plans.push({current, next});
      }
    }
    return plans;
  };
  const patchSections = plans => {
    for (const {current, next} of plans) {
      const active = current.contains(document.activeElement) ? document.activeElement : null;
      const saved = [...current.querySelectorAll('input,textarea,select')].filter(el => el.name && el.type !== 'hidden').map(el => ({name:el.name,id:el.id,value:el.value,checked:el.checked,active:el === active,quantity:el.hasAttribute('data-el-cart-quantity-input') || el.hasAttribute('data-line-key'),index:[...current.querySelectorAll('[name]')].filter(n => n.name === el.name).indexOf(el)}));
      const scroll = current.querySelector('.cart-drawer__inner')?.scrollTop;
      current.replaceChildren(...next.childNodes);
      saved.forEach(state => {
        const field = state.id ? document.getElementById(state.id) : [...current.querySelectorAll('[name]')].filter(n => n.name === state.name)[state.index];
        if (!field || !current.contains(field)) return;
        // Quantity always comes from Shopify; the quantity controller overlays newer queued intent.
        if (!state.quantity) { field.value = state.value; field.checked = state.checked; }
        if (state.active && !state.quantity) field.focus({preventScroll:true});
      });
      if (active?.name === 'checkout') current.querySelector('[name="checkout"]')?.focus({preventScroll:true});
      if (scroll != null && current.querySelector('.cart-drawer__inner')) current.querySelector('.cart-drawer__inner').scrollTop = scroll;
    }
  };
  const publish = (cart, ids, baseEvent) => {
    const plans = prepareSections(cart, ids); // Validate every section before touching any live container.
    patchSections(plans);
    document.querySelectorAll('cart-count').forEach(node => { node.itemCount = cart.item_count; });
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', {bubbles:true, detail:{cart, baseEvent}}));
    document.dispatchEvent(new CustomEvent('el:cart:synchronized', {detail:{cart}}));
  };
  const readback = async ids => {
    const cart = await request(root + 'cart.js');
    const url = new URL(location.pathname, location.origin);
    url.searchParams.set('sections', ids.join(','));
    // /cart honors content negotiation before section rendering; JSON Accept returns the cart object.
    cart.sections = await request(url.href, {headers:{Accept:'text/html'}});
    publish(cart, ids, 'quantity:reconciled');
    return cart;
  };
  const changeQuantity = (key, quantity) => {
    if (!key || !Number.isSafeInteger(quantity) || quantity < 0) return Promise.reject(failure('Enter a whole number of zero or more.', {code:'INVALID_QUANTITY', confirmed:true}));
    return serialize(async () => {
      const ids = sectionIds();
      status('Updating your cart…');
      try {
        const cart = await request(root + 'cart/change.js', {method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({id:key, quantity, sections:ids, sections_url:location.pathname})});
        publish(cart, ids, 'quantity:change');
        status('');
        return {cart, changed:false};
      } catch (error) {
        if (error.confirmed) {
          // Shopify may clamp stock before a 422 response; rejected does not mean unchanged.
          try { error.cart = await readback(ids); } catch (_) { error.recoverable = true; }
          status(error.message);
          throw error;
        }
        // A timeout or broken response may follow a successful write. Read only; never replay it.
        let cart;
        try { cart = await readback(ids); } catch (_) {}
        const message = cart
          ? 'The update could not be confirmed. Your current cart is shown; check the quantity before trying again.'
          : 'The update could not be confirmed. Check your connection and reload the cart before checking out.';
        status(message);
        throw failure(message, {code:'UNCONFIRMED_UPDATE', recoverable:true, confirmed:false, cart, cause:error});
      }
    });
  };
  const sync = () => {
    if (pendingSync) return pendingSync;
    pendingSync = (async () => {
      // Flush before entering the service queue: quantity writes use that queue themselves.
      await window.ELCartQuantity?.flush?.();
      return serialize(async () => {
        const ticket = version;
        status(config.checking);
        const before = await request(root + 'cart.js');
        const ids = sectionIds();
        const after = await request(root + 'cart/update.js', {method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({updates:{}, sections:ids, sections_url:location.pathname})});
        if (ticket !== version) throw failure('Cart changed during refresh.', {code:'CART_CHANGED'});
        let displayed = before;
        try { const snapshot = document.querySelector('.el-system-page--cart [data-el-cart-snapshot]') || document.querySelector('cart-drawer [data-el-cart-snapshot]'); displayed = JSON.parse(snapshot?.value || snapshot?.textContent || 'null') || before; } catch (_) {}
        const changed = differences(displayed, after);
        publish(after, ids, 'cart:recalculated');
        if (changed.length) reviewMessage = config.updated + ' ' + changed.join('; ') + '. ' + config.subtotal + ' ' + money(after.total_price) + '. ' + config.review;
        status(reviewMessage);
        return {changed:changed.length > 0, cart:after};
      });
    })().catch(error => { status(error.message && error.code === 'INVALID_QUANTITY' ? error.message : config.failed); throw error; }).finally(() => { pendingSync = null; });
    return pendingSync;
  };
  window.ELCartSync = {sync, changeQuantity, whenIdle:() => queue};
  document.addEventListener('cart:change', () => { version++; reviewMessage = ''; });
  document.addEventListener('click', event => {
    const trigger = event.target.closest('[aria-controls="cart-drawer"]');
    if (trigger && !document.querySelector('cart-drawer[open]')) sync().catch(() => {});
  }, true);
  document.addEventListener('submit', async event => {
    if (event.submitter?.name !== 'checkout' || !event.target.closest('cart-drawer,.el-system-page--cart')) return;
    if (event.target.dataset.elCheckoutReady === 'true') { delete event.target.dataset.elCheckoutReady; return; }
    event.preventDefault(); event.stopImmediatePropagation();
    if (checkoutPending) return;
    checkoutPending = true;
    const formId = event.target.id, drawer = Boolean(event.target.closest('cart-drawer'));
    try {
      const result = await sync();
      if (result.changed) return;
      await window.ELCartQuantity?.flush?.();
      const form = formId ? document.getElementById(formId) : event.target;
      const button = Array.from(form?.elements || []).find(control => control.name === 'checkout' && control.type === 'submit' && control.form === form);
      if (!form || !button || button.form !== form) throw failure(config.checkoutFailed || 'Checkout could not be prepared. Review your cart and try again.', {code:'CHECKOUT_FORM_MISSING', confirmed:true});
      form.dataset.elCheckoutReady = 'true';
      form.requestSubmit(button);
    } catch (error) {
      status(error.message || config.checkoutFailed || 'Checkout could not be prepared. Review your cart and try again.');
    } finally { checkoutPending = false; }
  }, true);
  window.addEventListener('pageshow', () => { if (document.querySelector('.el-system-page--cart,cart-drawer[open]')) sync().catch(() => {}); });
})();
